/**
 * O "Resumo do atendimento" da ficha (#1151): a consulta e os exames que as
 * rotinas trouxeram do SISAC para este card, cada um com o serviço, o médico,
 * as datas e o valor, e o total. O exame marcado reconhecido pela observação da
 * agenda vem com a marca "pela agenda" e o valor previsto, que não entra no total.
 *
 * Lido na hora do SISAC, nada guardado no CRM. Fora da clínica, sem a parte
 * ligada ou sem nenhum passo, o resumo não aparece; o SISAC fora vira aviso.
 */

import type { ReactNode } from 'react';
import { useAtendimentosDaOportunidade } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { dinheiroComCentavos } from '@/shared/lib/formato';
import { Faixa } from '@/shared/ui/estados/faixa';
import {
  PELA_AGENDA,
  nomeDoAtendimento,
  quandoNaClinica,
  valorEhPrevisto,
} from '@/entities/saude/atendimento';
import './resumo-do-atendimento.css';

export function ResumoDoAtendimento({ oportunidadeId }: { oportunidadeId: string }): ReactNode {
  const consulta = useAtendimentosDaOportunidade(oportunidadeId, true);

  if (consulta.isPending) {
    return <p className="resumo-atd__buscando">Buscando o atendimento no SISAC…</p>;
  }
  if (consulta.isError) {
    return (
      <Faixa tom="atencao">
        {recadoDeErro(consulta.error, {
          generica: 'Não foi possível buscar o atendimento no SISAC agora.',
        })}
      </Faixa>
    );
  }
  const { itens, total, sisac } = consulta.data;
  if (sisac === 'fora') {
    return (
      <Faixa tom="atencao">
        O SISAC não respondeu agora. O resumo do atendimento volta sozinho.
      </Faixa>
    );
  }
  if (itens.length === 0) return null;

  return (
    <section className="resumo-atd" aria-labelledby="resumo-atd-titulo">
      <h3 id="resumo-atd-titulo" className="ficha-op__versal">
        Resumo do atendimento
      </h3>
      <ol className="resumo-atd__lista">
        {itens.map((item) => {
          const marcado = quandoNaClinica(item.marcado_para);
          const agendado = quandoNaClinica(item.agendado_em);
          const valor = dinheiroComCentavos(item.valor);
          return (
            <li key={item.referencia} className="resumo-atd__item">
              <span className="resumo-atd__cabeca">
                <span className="resumo-atd__nome">
                  {nomeDoAtendimento(item)}
                  {item.servico_da_observacao && (
                    <span className="resumo-atd__origem" title={PELA_AGENDA}>
                      {' '}
                      pela agenda
                    </span>
                  )}
                </span>
                {valor && (
                  <span className="resumo-atd__valor">
                    {valor}
                    {valorEhPrevisto(item) && (
                      <span className="resumo-atd__previsto"> previsto</span>
                    )}
                  </span>
                )}
              </span>
              <dl className="resumo-atd__dados">
                {item.medico && (
                  <div>
                    <dt>Médico</dt>
                    <dd>{item.medico}</dd>
                  </div>
                )}
                {marcado && (
                  <div>
                    <dt>Marcado para</dt>
                    <dd>{marcado}</dd>
                  </div>
                )}
                {agendado && (
                  <div>
                    <dt>Agendado em</dt>
                    <dd>{agendado}</dd>
                  </div>
                )}
              </dl>
            </li>
          );
        })}
      </ol>
      {total && (
        <p className="resumo-atd__total">
          <span>Total</span>
          <span className="resumo-atd__valor">{dinheiroComCentavos(total)}</span>
        </p>
      )}
    </section>
  );
}
